import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import classNames from 'classnames';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { BASE_URL, IMAGE_PATHS } from '../../../../constants/paths.constants';
import {
  purple,
  black,
  transparentYellow,
  transparentGrey,
  universalStyles,
  backgroundPurple,
} from '../../../../app/theme';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  yellowBox: {
    backgroundColor: transparentYellow,
    border: `1px solid ${transparentGrey}`,
    width: '100%',
    height: 121,
    marginTop: 20,
  },
  contentDivContainer: {
    width: '10%',
    height: '100%',
    display: 'inline-block',
    textAlign: 'center',
  },
  contentDiv: {
    width: 2,
    height: 74,
    backgroundColor: purple,
    display: 'inline-block',
    verticalAlign: 'middle',
    marginTop: 23,
    marginBottom: 23,
  },
  contentBox: {
    width: '45%',
    height: '100%',
    display: 'inline-block',
    verticalAlign: 'top',
    textAlign: 'center',
  },
  text: {
    marginTop: 30,
  },
  listHeader: {
    marginTop: 30,
    marginBottom: 10,
    fontWeight: 600,
  },
  smallTopMargin: {
    marginTop: 15,
  },
  smallBottomMargin: {
    marginBottom: 15,
  },
  listSection: {
    marginTop: 50,
    marginBottom: 50,
  },
  image: {
    paddingTop: 20,
    width: '100%',
  },
  bottomImage: {
    padding: 0,
    width: '80%',
    marginLeft: '10%',
  },
  sectionHeader: {
    marginBottom: 25,
  },
  checkmark: {
    width: 78,
    height: 78,
    display: 'inline',
    verticalAlign: 'middle',
    paddingTop: 20,
    paddingBottom: 20,
  },
  checkmarkCaption: {
    color: black,
    fontSize: 18,
    fontWeight: 600,
    lineHeight: 1.2,
    display: 'inline-block',
    verticalAlign: 'middle',
    textAlign: 'left',
    width: 320,
    paddingTop: 20,
    paddingBottom: 20,
    paddingLeft: 25,
    paddingRight: 25,
  },
  purpleContainer: {
    display: 'flex',
    padding: '25px 25px 25px 25px',
    backgroundColor: backgroundPurple,
  },
  purpleCol: {
    flex: 1,
    borderRight: `1px solid ${purple}`,
    padding: '0px 25px 0px 25px',
    display: 'flex',
    flexDirection: 'column',
  },
  purpleRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    alignItems: 'center',
  },
  flextxt: {
    flex: 1,
    paddingLeft: 20,
    paddingRight: 20,
    fontWeight: 600,
    verticalAlign: 'middle',
    marginBottom: 0,
    marginTop: 0,
  },
  lastbox: {
    border: 'none',
  },
  headerImage: {
    display: 'block',
    width: '100%',
  },
}));

type AcquisitionPage09Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage09 = ({ setNextDisabled }: AcquisitionPage09Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.grayBox}>
        <img
          className={classes.headerImage}
          src={`${BASE_URL}${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_4.path}`}
          alt={`${IMAGE_PATHS.STEPS[1].ACQUISITION_STEP_4.alt}`}
        />
        <div className={classes.whiteBox}>
          <Typography variant="h2">STEP 4:</Typography>
          <Typography variant="h3" className={classes.sectionHeader}>
            Image Acquisition
          </Typography>
          <Typography variant="h5" className={classNames(classes.text, classes.smallBottomMargin)}>
            Image Acquisition Is Important for Biodistribution
          </Typography>

          <Typography variant="body1" className={classNames(classes.text, classes.smallTopMargin)}>
            Recommended to scan for
            <b> 5 minutes per bed </b>
            position over pelvis (ie, pubic symphysis to iliac crest) to increase sensitivity of the detection of
            prostate cancer recurrence in sites typical for such recurrence
          </Typography>
          <Typography variant="body1" className={classes.text}>
            <i>
              Boxes represent the estimated area of image acquisition at a certain time point; rising window location is
              subject to change based on manufacturer specifications for the scanner.
            </i>
          </Typography>

          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.IMAGE_ACQ_TIME.path}`}
            alt={`${IMAGE_PATHS.IMAGE_ACQ_TIME.alt}`}
          />

          <Typography variant="body1" className={classes.text}>
            5 minutes per bed position is recommended, although this may be reduced to
            <b> 3 minutes per bed </b>
            position (if PET/CT scanner allows this adjustment and site is experienced with such image acquisition
            procedures)
          </Typography>
          <Typography variant="body1" className={classes.text}>
            <i>
              Boxes represent the estimated area of image acquisition at a certain time point; rising window location is
              subject to change based on manufacturer specifications for the scanner.
            </i>
          </Typography>

          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.IMAGE_ACQ_TIME2.path}`}
            alt={`${IMAGE_PATHS.IMAGE_ACQ_TIME2.alt}`}
          />

          <div className={classes.listSection}>
            <Typography variant="body1" className={classes.listHeader}>
              <b>Typical total scan time is between 20 to 30 minutes</b>
            </Typography>
            <ul className={classes.list}>
              <li>
                Actual scan time is typically dependent on scanner type, scan length, and time per bed position/ bed
                speed (scanners with continuous bed motion)
              </li>
              <li>
                <span>
                  Recommended to scan for
                  <b> 5 minutes per bed </b>
                  position over pelvis
                </span>
              </li>
              <ul className={classes.nestedList}>
                <li>
                  However, image acquisition time is scanner specific; start time of scan after injection (3 to 5
                  minutes, target 4 minutes) is more important
                </li>
                <li>
                  For remaining bed positions (ie, iliac crest to base of skull), 5 minutes per bed position is
                  recommended, although this may be reduced to 3 minutes per bed position (if PET/CT scanner allows this
                  adjustment and site is experienced with such image acquisition procedures)
                </li>
              </ul>
            </ul>
          </div>
          <Typography variant="h5">Considerations for Technologists</Typography>
          <div className={classNames(classes.purpleContainer)}>
            <div className={classNames(classes.purpleCol)}>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>Location of IV placement</Typography>
              </div>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>
                  Patient’s ability to raise their arms above their head
                </Typography>
              </div>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>Claustrophobia</Typography>
              </div>
            </div>
            <div className={classNames(classes.purpleCol, classes.lastbox)}>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>
                  Assay, residual, and total activity injected
                </Typography>
              </div>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>
                  Injection and image acquisition start time
                </Typography>
              </div>
              <div className={classNames(classes.purpleRow)}>
                <img
                  className={classNames(classes.checkmark)}
                  src={`${BASE_URL}${IMAGE_PATHS.YELLOW_CHECKMARK.path}`}
                  alt={`${IMAGE_PATHS.YELLOW_CHECKMARK.alt}`}
                />
                <Typography className={classNames(classes.text, classes.flextxt)}>
                  Time per bed (if variable)
                </Typography>
              </div>
            </div>
          </div>
        </div>
      </div>
      <LearningCheckQuestions module="axuminAcquisition" page={9} setNextDisabled={setNextDisabled} trainingId={1} />
    </>
  );
};

export default AcquisitionPage09;
